/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Constants } from 'app/core/models/constants';
import { ImageUploadErrorType } from 'app/shared/models/image-upload-error-type';

const customValueProvider = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => ImageSelectorComponent),
	multi: true,
};
const customValidateProvider = {
	provide: NG_VALIDATORS,
	useExisting: forwardRef(() => ImageSelectorComponent),
	multi: true,
};

@Component({
	selector: 'image-selector',
	templateUrl: './image-selector.component.html',
	styles: [
		`
			.image-control-button {
				cursor: pointer;
			}
			.responsive-image {
				width: 100%;
				height: auto;
				display: block;
			}
		`,
	],
	providers: [customValueProvider, customValidateProvider],
	standalone: false,
})
export class ImageSelectorComponent implements ControlValueAccessor {
	@Input() allowedMimeTypes = ['image/png', 'image/jpeg'];
	@Input() maxFileSizeMB = 10;
	maxFileSizeKB = this.maxFileSizeMB * 1024;
	maxFileSize = this.maxFileSizeKB * 1024;
	@Output() invalidErrorType = new EventEmitter<ImageUploadErrorType>();
	@Output() isValid = new EventEmitter<boolean>();
	@Output() validationMessage = new EventEmitter<string>();
	@Output() imageDeleted = new EventEmitter<boolean>();
	dataForm: FormGroup;
	@Input() startTabIndex = 1;

	private emptyImage = Constants.ImageSelector.emptyImage;
	writtenValue: string;
	imageAsUrl: SafeUrl = null;

	constructor(private sanitizer: DomSanitizer, private elementRef: ElementRef) {}

	get imageLoaded(): boolean {
		return this.imageAsUrl !== null;
	}

	get image(): AbstractControl {
		return this.dataForm.controls['image'];
	}

	propagateChange: any = () => {};
	onTouched: any = () => {
		console.log('touched');
	};

	writeValue(image: string): void {
		if (!image) {
			this.updateWithEmptyImage();
		} else {
			this.writtenValue = image;

			// if the image is already a safe url then just set it
			if (image.startsWith('data:image/')) {
				this.imageAsUrl = this.getImageAsSafeUrl(image);
			} else {
				this.imageAsUrl = this.getImageAsSafeUrlFromBytes(image);
			}
			// convert the supplied bytes to a safe url wghich will be bound to the image
		}
	}

	private getImageAsSafeUrlFromBytes(image) {
		let objectURL = 'data:image/png;base64,' + image;
		return this.sanitizer.bypassSecurityTrustUrl(objectURL);
	}
	private getImageAsSafeUrl(image) {
		return this.sanitizer.bypassSecurityTrustUrl(image);
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	notifyChanged(value: string) {
		this.propagateChange(value);
		this.onTouched();
	}

	addImage(event) {
		let imageFileInput = this.elementRef.nativeElement.querySelector('#selectImage');

		if (imageFileInput) {
			const changeListener = async () => {
				let file = imageFileInput.files[0];
				this.validate(file);
				const fileAsBase64 = await this.getFileAsBase64(file);

				this.imageAsUrl = fileAsBase64;

				this.notifyChanged(fileAsBase64);
				// TODO: [Technical Debt][dpb][2023-11-02] existing lines unchanged
				imageFileInput.removeEventListener('change', changeListener);
				imageFileInput.value = null;
			};

			imageFileInput.addEventListener('change', changeListener);

			imageFileInput.click();
		}
	}

	private validate(fileItem: any): void {
		let validationMessage = '';

		if (!fileItem) return;
		if (!fileItem.type) return;

		if (this.allowedMimeTypes.length > 0) {
			const allowedType = this.allowedMimeTypes.find((f) => f == fileItem.type);
			if (!allowedType) {
				validationMessage = "Cannot add '" + fileItem.name + "'. File type '" + fileItem.type + "' is not allowed";
				this.invalidErrorType.emit(ImageUploadErrorType.InvalidMimeType);
				this.isValid.emit(false);
				this.validationMessage.emit(validationMessage);
				return;
			}
		}

		if (fileItem.size > this.maxFileSize) {
			validationMessage = "Cannot add '" + fileItem?.name + "'. File cannot exceed " + this.maxFileSizeMB + ' MB';
			this.invalidErrorType.emit(ImageUploadErrorType.FileTooLarge);
			this.isValid.emit(false);
			this.validationMessage.emit(validationMessage);
			return;
		}

		this.isValid.emit(true);
		this.validationMessage.emit('');
		this.invalidErrorType.emit(null);
	}

	removeImage(event) {
		this.updateWithEmptyImage();
		this.imageDeleted.emit(true);
		this.notifyChanged(null);
	}

	private updateWithEmptyImage() {
		this.writtenValue = null;
		const encodeString = this.emptyImage;
		this.imageAsUrl = this.getImageAsSafeUrlFromBytes(encodeString);
	}

	getFileAsBase64(file: File): Promise<string> {
		const reader = new FileReader();
		return new Promise<string>((resolve) => {
			reader.onload = (ev) => {
				resolve(ev.target.result as string);
			};
			reader.readAsDataURL(file);
		});
	}
}
